import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, MouseEvent as ReactMouseEvent } from "react";
import type { Member, Workflow } from "../api/types";
import { useMembers, usePatchAssignment, useTimelineNorm, useToggleAvailability } from "../lib/proofers";
import { avatarColor, initials } from "../lib/avatar";
import { useDayTick } from "../lib/today";

/**
 * The Workflows Timeline — the board's rows laid on a date axis.
 *
 * The board answers "what's in flight"; this answers "what's due when, and is
 * anyone overloaded". Two things make it worth a separate view:
 *
 * * **Grouping is a lens, not a layout.** The same bars regroup by status /
 *   proofer / client, so one render path serves a delivery schedule AND a
 *   staffing chart.
 * * **Capacity.** Every row carries finished-hours (`runtime_seconds`, or the
 *   linked project's measured runtime), so booked hours per person per week is
 *   computable — the one question the board structurally cannot answer.
 *
 * Both date fields are optional and plenty of rows have neither, so undated work
 * is surfaced in its own tray rather than silently dropped: a schedule that
 * quietly hides half the slate is worse than no schedule.
 */

/** Finished hours one proofer gets through in a week. A working assumption, not
 *  a measured fact — it only sets where the capacity strip turns red, and it's
 *  the first thing to tune once real throughput is known. The booked figure is
 *  always shown regardless, so the strip stays useful even if this is wrong. */
const WEEK_CAPACITY_H = 25;

/** A day never renders narrower than this. The axis is percentage-based, so a
 *  long span would otherwise squeeze days to a couple of pixels and the grid
 *  would stop meaning anything — instead the view widens and scrolls, which is
 *  what keeps single days readable at ANY span.
 *
 *  Sized from the load strips, which print a figure in every day cell: at 9px
 *  JetBrains Mono a 4-character label ("246%", "10.2") measures 21.6px and the
 *  worst case ("1000%") 27px. The old 22px left four tenths of a pixel of slack
 *  and clipped five-character labels outright. */
const MIN_DAY_PX = 30;
/** Below this the day numbers are dropped (the gridlines still mark each day) —
 *  cramming unreadable digits is worse than leaving them out. Kept just under
 *  MIN_DAY_PX so the reserved width always clears it: a floor that guaranteed
 *  less room than the numbers need would mean they never appeared. */
const DAY_NUM_PX = 20;
/** Below this a load cell can't hold its figure, so the strip drops to colour
 *  only rather than clipping digits — the same trade the axis makes with day
 *  numbers. MIN_DAY_PX keeps us above it in practice; this is the guard that
 *  makes that a property of the code rather than a coincidence. */
const LOAD_NUM_PX = 28;

/** The frozen name column. It stays put while the date axis scrolls, so you can
 *  always tell which project a bar belongs to — the whole point of the view.
 *
 *  The default grew (and the key rolled to v2, so a stored 172 doesn't strand
 *  anyone on it) when the rate and total moved OFF the bars and into this
 *  column: at the old width a title was two letters and an ellipsis. The two
 *  thresholds below drop the least load-bearing items first rather than letting
 *  a narrow column eat the title, which is the one thing that must survive. */
const NAME_W_KEY = "wf_tl_namew_v2";
const NAME_W_DEFAULT = 300;
const NAME_W_MIN = 130;
const NAME_W_MAX = 520;
/** Below this the total is dropped — the rate is what explains the load strip. */
const NAME_TOTAL_W = 250;
/** Below this the proofer avatars go too; the bar's tooltip still names them. */
const NAME_WHO_W = 210;
function loadNameW(): number {
  const n = Number(localStorage.getItem(NAME_W_KEY));
  return Number.isFinite(n) && n >= NAME_W_MIN && n <= NAME_W_MAX ? n : NAME_W_DEFAULT;
}

const MS = 86400000;
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pad2 = (n: number) => String(n).padStart(2, "0");
const parseD = (s: string): Date => { const p = s.split("-"); return new Date(+p[0], +p[1] - 1, +p[2]); };
const dayStart = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
/** Monday of the week containing `d` — the capacity bucket + axis tick. */
function weekStart(d: Date): Date {
  const x = dayStart(d);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
}
const weekKey = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
/** The wire format both date fields use. */
const iso = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
/** Pointer travel below this is a click, not a drag — so a pixel of hand tremor
 *  on the move band can't quietly rewrite a title's dates. */
const DRAG_SLOP_PX = 4;

/** Finished hours a row represents — the same figure the board's runtime shows. */
function hoursOf(w: Workflow): number {
  const s = w.runtime_seconds ?? w.project_runtime_s ?? 0;
  return s > 0 ? s / 3600 : 0;
}

/** Who a row is booked against. Real assignments win over the free-text field —
 *  same precedence the board's Proofer column uses. */
function peopleOf(w: Workflow): string[] {
  const real = (w.assigned_proofers ?? []).map((a) => a.name).filter(Boolean);
  if (real.length) return real;
  const typed = (w.proofer || "").trim();
  if (!typed || typed.toUpperCase() === "NA") return [];
  return [typed];
}

export type GroupBy = "status" | "proofer" | "client";

/** A row's span. `inferred` marks a start we derived rather than one the PM set,
 *  so the bar can say so instead of implying a date that was never entered. */
interface Span { from: Date; to: Date; inferred: boolean }

/**
 * A row is schedulable if it has a delivery date. A missing START is recoverable
 * — we back-date from the finished hours (at a nominal working rate) so the bar
 * still carries the right weight — but a missing DELIVERY leaves nothing to
 * anchor to, so those rows go to the undated tray.
 */
function spanOf(w: Workflow): Span | null {
  if (!w.delivery_date) return null;
  const to = parseD(w.delivery_date);
  if (w.start_date) {
    const from = parseD(w.start_date);
    return from <= to ? { from, to, inferred: false } : { from: to, to, inferred: false };
  }
  // ~6 finished hours a working day is the studio's rough throughput; it only
  // sets the bar's length, never a stored date.
  const days = Math.max(1, Math.ceil(hoursOf(w) / 6) || 1);
  const from = new Date(to.getTime() - (days - 1) * MS);
  return { from, to, inferred: true };
}

/**
 * A title being carried somewhere — the only VERTICAL gesture in the view, and
 * deliberately not on the bar: grips resize it, the move band reschedules it,
 * and the day cells park it, so a fourth meaning would have nowhere to live.
 * `reassign` starts from the accent handle in the frozen column, `place` from a
 * chip in the undated tray.
 */
interface CarryState {
  kind: "reassign" | "place";
  id: string;
  label: string;
  /** Whose card it left (reassign only) — the assignment that moves. */
  from?: string;
  x: number; y: number;
  moved: boolean;
  /** Live hit-test result, refreshed on every move. */
  toPerson?: string | null;
  toDay?: number | null;
  ok: boolean;
}

/** How long a dropped undated title runs, in days. It has no measured runtime
 *  to size it from, so this is a starting shape you then drag — not a guess
 *  dressed up as a plan. */
const PLACED_DAYS = 3;
/** Pointer this close to an edge while carrying and the view scrolls: with more
 *  than a few proofers the card you're aiming at is off-screen, and a drag you
 *  can't complete is worse than no drag. */
const EDGE_PX = 64;
const EDGE_SPEED = 16;

/** A run of days being switched off (or back on) for one row. `on` is fixed at
 *  pointerdown from whatever the first day flips to, so dragging across a mixed
 *  run paints it all one way instead of alternating cell by cell. */
interface PaintState { id: string; targets: string[]; anchor: number; day: number; on: boolean }

type DragMode = "move" | "start" | "end";
interface DragState {
  id: string; mode: DragMode; from: Date; to: Date; inferred: boolean;
  originX: number; days: number; moved: boolean;
}

export function TimelineView({
  rows, statusMeta, statusOrder, onOpen, onOpenProject, onPatch,
  groupBy: groupByProp, onGroupBy, mode: modeProp, onMode,
}: {
  rows: Workflow[];
  statusMeta: (s: string) => { label: string; bar: string; bg: string; fg: string };
  statusOrder: string[];
  onOpen: (id: string) => void;
  /** A linked row's name goes straight to the project hub. */
  onOpenProject: (projectId: string) => void;
  onPatch: (id: string, patch: {
    start_date?: string | null;
    delivery_date?: string | null;
    /** Whole map, replaced — see `Workflow.paused`. */
    paused?: Record<string, string[]>;
    /** Free-text assignee, for rows with no linked project. */
    proofer?: string | null;
  }) => void;
  /** Lifted so a saved view can capture them; uncontrolled when not supplied. */
  groupBy?: GroupBy;
  onGroupBy?: (g: GroupBy) => void;
  mode?: "load" | "hours";
  onMode?: (m: "load" | "hours") => void;
}) {
  const [nameW, setNameW] = useState<number>(loadNameW);
  useEffect(() => { try { localStorage.setItem(NAME_W_KEY, String(nameW)); } catch { /* ignore */ } }, [nameW]);

  // Same drag idiom as the board's column resize, so the two feel identical.
  const startNameResize = (e: ReactMouseEvent) => {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX; const startW = nameW;
    document.body.classList.add("wf-resizing");
    const mv = (ev: MouseEvent) =>
      setNameW(Math.max(NAME_W_MIN, Math.min(NAME_W_MAX, Math.round(startW + (ev.clientX - startX)))));
    const up = () => {
      document.removeEventListener("mousemove", mv);
      document.removeEventListener("mouseup", up);
      document.body.classList.remove("wf-resizing");
    };
    document.addEventListener("mousemove", mv);
    document.addEventListener("mouseup", up);
  };

  // Hours opens by default. Workload (% of a day / proofers needed) is the more
  // pointed reading, but hours is the raw fact people check first; the toggle is
  // right there.
  const [modeOwn, setModeOwn] = useState<"load" | "hours">("hours");
  const mode = modeProp ?? modeOwn;
  const setMode = (m: "load" | "hours") => { setModeOwn(m); onMode?.(m); };
  const [hoverDay, setHoverDay] = useState<number | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  // Painting a run of paused days. Held locally and committed once on pointerup:
  // a PATCH per day would be a dozen round-trips for one "park this for a week".
  const [paint, setPaint] = useState<PaintState | null>(null);
  // The same state, mutated SYNCHRONOUSLY as the pointer crosses each day. A
  // quick flick can dispatch the last move and the pointerup in one tick, and
  // React hasn't re-rendered by then — so committing from `paint` silently drops
  // the day you released on. The ref is what pointerup reads; `paint` only
  // repaints.
  const paintLive = useRef<PaintState | null>(null);
  // Carrying a title: to another proofer, or out of the undated tray onto a
  // date. Same ref-beside-state idiom as the paint, and for the same reason —
  // the drop reads the ref, so a fast release can't commit a stale target.
  const [carry, setCarry] = useState<CarryState | null>(null);
  const carryLive = useRef<CarryState | null>(null);
  // `carry` is cleared on pointerup but the browser fires `click` after it, so
  // an undated chip you dragged would also open its row. Holds the id of the
  // chip that was dragged — NOT a bare flag: a flag set by any drag anywhere
  // (a reassign, a different chip) is still set when you next click a chip, and
  // eats that click.
  const justCarried = useRef<string | null>(null);
  // A title that just landed jumps to the group it really belongs to (we never
  // change the proofer on a drop), so it has to say where it went or the jump
  // reads as a failed drag.
  const [flashId, setFlashId] = useState<string | null>(null);
  // Proofer first, and the lens the view opens on: it's the reading people
  // reach for, and it's the only lens where a title can be dragged from one
  // person to another. (The weekly Capacity block is hidden here on purpose —
  // the per-day group strips already say it, per day.)
  const [groupByOwn, setGroupByOwn] = useState<GroupBy>("proofer");
  const groupBy = groupByProp ?? groupByOwn;
  const setGroupBy = (g: GroupBy) => { setGroupByOwn(g); onGroupBy?.(g); };
  // Measured track width — decides whether day numbers fit. Measured rather than
  // assumed because the panel width varies with the sidebar and the viewport.
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [trackPx, setTrackPx] = useState(0);
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => setTrackPx(entries[0].contentRect.width));
    ro.observe(el);
    setTrackPx(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  // Open with TODAY centred in the visible track. The axis spans every bar, so a
  // slate with months of finished work would otherwise open on the past and make
  // you scroll to find the present. Runs once, on the first render where the
  // widths are real — after that the scroll position is the user's.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const centred = useRef(false);

  const dated = useMemo(
    () => rows.map((w) => ({ w, span: spanOf(w) })).filter((r): r is { w: Workflow; span: Span } => !!r.span),
    [rows],
  );
  const undated = useMemo(() => rows.filter((w) => !spanOf(w)), [rows]);

  // The axis covers every bar plus today, padded to whole weeks so the ticks
  // line up with the capacity buckets underneath.
  // The axis is built around TODAY - where the line sits, what counts as past,
  // and every "over capacity" judgement that follows from it. It is memoised on
  // the data, so without this it would keep yesterday's idea of today for as
  // long as the tab stayed open.
  const dayTick = useDayTick();
  const axis = useMemo(() => {
    const today = dayStart(new Date());
    if (!dated.length) return null;
    let lo = dated[0].span.from, hi = dated[0].span.to;
    for (const { span } of dated) { if (span.from < lo) lo = span.from; if (span.to > hi) hi = span.to; }
    if (today < lo) lo = today;
    if (today > hi) hi = today;
    const first = weekStart(lo);
    const last = weekStart(new Date(hi.getTime() + 7 * MS));
    const weeks: Date[] = [];
    for (let d = new Date(first); d <= last; d = new Date(d.getTime() + 7 * MS)) weeks.push(new Date(d));
    // Rounded, because these are LOCAL midnights: the week the clocks change is
    // 25 or 23 hours long, so the raw division lands on 126.04 days and
    // new Array(126.04) throws — the whole view white-screens for any board
    // whose axis happens to span the change. Every other index here already
    // rounds; this one is also an array length, which is why it was the one
    // that bit.
    const total = Math.max(1, Math.round((last.getTime() + 7 * MS - first.getTime()) / MS));
    // Where today sits in the day-indexed series. Anything before it has
    // already happened and can no longer be planned around.
    const todayIdx = Math.round((today.getTime() - first.getTime()) / MS);
    return { first, weeks, total, today, todayIdx };
  }, [dated, dayTick]);

  useEffect(() => {
    if (centred.current || !axis || !trackPx) return;
    const el = scrollRef.current;
    if (!el) return;
    // The frozen name column covers the left of the viewport, so the visible
    // stretch of track is what's left beside it — centre today in THAT, not in
    // the container, or it lands under the names.
    const visible = el.clientWidth - nameW;
    if (visible <= 0) return;
    const todayInTrack = ((axis.today.getTime() - axis.first.getTime()) / MS / axis.total) * trackPx;
    const max = Math.max(0, el.scrollWidth - el.clientWidth);
    el.scrollLeft = Math.max(0, Math.min(todayInTrack - visible / 2, max));
    centred.current = true;
  }, [axis, trackPx, nameW]);

  const { data: rosterData } = useMembers();
  const { norm: normRaw, setNorm } = useTimelineNorm();
  const norm = normRaw && normRaw > 0 ? normRaw : 3;
  const toggleAvail = useToggleAvailability();
  const reassignM = usePatchAssignment();

  const memberByName = useMemo(() => {
    const m = new Map<string, Member>();
    (rosterData?.members || []).forEach((x) => { if (x.name) m.set(x.name.trim().toLowerCase(), x); });
    return m;
  }, [rosterData]);

  /**
   * Audio-hours per day, per group — and the whole reason this view can answer
   * "is anyone overloaded".
   *
   * The atom is (title x PROOFER), not the title: each proofer's share spreads
   * over THEIR available days inside that title's span. That's what stops one
   * person's day off moving somebody else's hours on a shared title, and it's
   * what keeps the totals honest when availability is in play.
   *
   * TWO things take a day away from that pair, and they're different questions:
   * the proofer is off that day (person-wide, hits every title they have), or
   * this title is parked for them (`w.paused`, hits only this one). The second
   * is why a team-proofed title keeps moving when one of its proofers steps
   * away — the other's share is untouched.
   *
   * Spreading conserves hours — a day off doesn't delete work, it pushes it
   * onto the remaining days. Where a share has nowhere left to land at all,
   * `lost` carries it so the UI can say so rather than quietly dropping it.
   */
  const dist = useMemo(() => {
    const zero = () => new Array(axis ? axis.total : 0).fill(0) as number[];
    const empty = { vals: zero(), lost: 0 };
    if (!axis) return {
      seriesFor: () => empty, total: empty, personDaily: new Map<string, number[]>(),
      rateOf: () => 0, rateAt: () => 0,
    };

    const offIdx = (name: string): Set<number> => {
      const m = memberByName.get(name.trim().toLowerCase());
      const out = new Set<number>();
      for (const d of m?.unavailable || []) {
        const i = Math.round((parseD(d).getTime() - axis.first.getTime()) / MS);
        if (i >= 0 && i < axis.total) out.add(i);
      }
      return out;
    };
    const offCache = new Map<string, Set<number>>();
    const offFor = (name: string) => {
      let s = offCache.get(name);
      if (!s) { s = offIdx(name); offCache.set(name, s); }
      return s;
    };

    /** Every day this (title, person) pair doesn't work: the person's own days
     *  off plus the days this title is parked for them. Cached per pair — the
     *  union is recomputed on nothing else, and scrubbing hits it constantly. */
    const skipCache = new Map<string, Set<number>>();
    const skipFor = (w: Workflow, person: string | null): Set<number> => {
      const key = `${w.id}|${person ?? ""}`;
      let s = skipCache.get(key);
      if (!s) {
        s = new Set(person ? offFor(person) : []);
        for (const d of (person ? w.paused?.[person] : undefined) || []) {
          const i = Math.round((parseD(d).getTime() - axis.first.getTime()) / MS);
          if (i >= 0 && i < axis.total) s.add(i);
        }
        skipCache.set(key, s);
      }
      return s;
    };

    /** One (title, person) share laid across the days that pair actually works. */
    const spread = (w: Workflow, span: Span, person: string | null, share: number, into: number[]): number => {
      const off = skipFor(w, person);
      const from = Math.max(0, Math.round((span.from.getTime() - axis.first.getTime()) / MS));
      const to = Math.min(axis.total - 1, Math.round((span.to.getTime() - axis.first.getTime()) / MS));
      const days: number[] = [];
      for (let i = from; i <= to; i++) if (!off.has(i)) days.push(i);
      if (!days.length) return share;                  // nowhere to put it
      const v = share / days.length;
      days.forEach((i) => { into[i] += v; });
      return 0;
    };

    const addRow = (r: { w: Workflow; span: Span }, into: number[], only: string | null): number => {
      const who = peopleOf(r.w);
      const h = hoursOf(r.w);
      if (!h) return 0;
      if (!who.length) return only ? 0 : spread(r.w, r.span, null, h, into);
      let lost = 0;
      who.forEach((p) => {
        if (only && p !== only) return;
        lost += spread(r.w, r.span, p, h / who.length, into);
      });
      return lost;
    };

    const seriesFor = (rows: { w: Workflow; span: Span }[], person: string | null) => {
      const vals = zero();
      let lost = 0;
      rows.forEach((r) => { lost += addRow(r, vals, person); });
      return { vals, lost };
    };

    const total = seriesFor(dated, null);

    const personDaily = new Map<string, number[]>();
    dated.forEach((r) => {
      peopleOf(r.w).forEach((p) => {
        if (!personDaily.has(p)) personDaily.set(p, zero());
        addRow(r, personDaily.get(p)!, p);
      });
    });

    /** One (row, person) pair's constant contribution, and whether a given day
     *  gets it. The rate is constant because the hours spread evenly, which is
     *  what lets a single number on the bar explain the whole strip. Cached per
     *  pair so scrubbing doesn't recompute spans on every pointer move. */
    const cache = new Map<string, { from: number; to: number; off: Set<number>; rate: number }>();
    const one = (r: { w: Workflow; span: Span }, person: string | null) => {
      const key = `${r.w.id}|${person ?? ""}`;
      let e = cache.get(key);
      if (!e) {
        const who = peopleOf(r.w);
        const h = hoursOf(r.w);
        const from = Math.max(0, Math.round((r.span.from.getTime() - axis.first.getTime()) / MS));
        const to = Math.min(axis.total - 1, Math.round((r.span.to.getTime() - axis.first.getTime()) / MS));
        const off = skipFor(r.w, person);
        let n = 0;
        for (let i = from; i <= to; i++) if (!off.has(i)) n++;
        const share = who.length ? h / who.length : h;
        const dead = !h || (person !== null && who.length > 0 && !who.includes(person));
        e = { from, to, off, rate: dead || !n ? 0 : share / n };
        cache.set(key, e);
      }
      return e;
    };
    /** The rate to print ON the bar — constant, so no day is needed. */
    const rateOf = (r: { w: Workflow; span: Span }, person: string | null) => {
      const who = peopleOf(r.w);
      if (person || !who.length) return one(r, person).rate;
      // No person in scope (status / client lens): the row's daily load is every
      // proofer's share added up, and they can have different days off.
      return who.reduce((a, pn) => a + one(r, pn).rate, 0);
    };
    /** The same, for one specific day — zero outside the span or on a day off. */
    const rateAt = (r: { w: Workflow; span: Span }, person: string | null, i: number) => {
      const who = peopleOf(r.w);
      const pick = (pn: string | null) => {
        const e = one(r, pn);
        return i >= e.from && i <= e.to && !e.off.has(i) ? e.rate : 0;
      };
      if (person || !who.length) return pick(person);
      return who.reduce((a, pn) => a + pick(pn), 0);
    };

    return { seriesFor, total, personDaily, rateOf, rateAt };
  }, [dated, axis, memberByName]);

  /** The old week-bucketed view, derived from the daily distribution so there
   *  is one source of truth and days off show through here as well. */
  const capacity = useMemo(() => {
    const out = new Map<string, Map<string, number>>();
    if (!axis) return out;
    dist.personDaily.forEach((vals, person) => {
      const weeks = new Map<string, number>();
      vals.forEach((v, i) => {
        if (!v) return;
        const k = weekKey(weekStart(new Date(axis.first.getTime() + i * MS)));
        weeks.set(k, (weeks.get(k) ?? 0) + v);
      });
      if (weeks.size) out.set(person, weeks);
    });
    return out;
  }, [dist, axis]);

  const groups = useMemo(() => {
    const g = new Map<string, { w: Workflow; span: Span }[]>();
    for (const r of dated) {
      const keys =
        groupBy === "status" ? [r.w.status || "announced"]
          : groupBy === "client" ? [(r.w.client || "").trim() || "— no client —"]
            : (peopleOf(r.w).length ? peopleOf(r.w) : ["— unassigned —"]);
      for (const k of keys) g.set(k, [...(g.get(k) ?? []), r]);
    }
    const keys = [...g.keys()].sort((a, b) => {
      if (groupBy === "status") return statusOrder.indexOf(a) - statusOrder.indexOf(b);
      if (a.startsWith("—")) return 1;
      if (b.startsWith("—")) return -1;
      return a.localeCompare(b);
    });
    return keys.map((k) => ({ key: k, rows: g.get(k)!.slice().sort((x, y) => +x.span.to - +y.span.to) }));
  }, [dated, groupBy, statusOrder]);

  // A painted run of paused days commits on pointerup — one PATCH rather than one
  // per day, so parking a week is a single round-trip. Declared BEFORE the early
  // return below so the hook order never depends on there being work to show.
  const painting = !!paint;
  useEffect(() => {
    if (!painting || !axis) return;
    const onUp = () => {
      const p = paintLive.current;
      const w = p ? rows.find((r) => r.id === p.id) : undefined;
      if (p && w) {
        const lo = Math.min(p.anchor, p.day);
        const hi = Math.max(p.anchor, p.day);
        const next: Record<string, string[]> = { ...(w.paused || {}) };
        p.targets.forEach((person) => {
          const cur = new Set(next[person] || []);
          for (let i = lo; i <= hi; i++) {
            const d = iso(new Date(axis.first.getTime() + i * MS));
            if (p.on) cur.add(d); else cur.delete(d);
          }
          const arr = [...cur].sort();
          // Drop the key rather than leave an empty list behind, so "nothing
          // paused" has one representation instead of two.
          if (arr.length) next[person] = arr; else delete next[person];
        });
        onPatch(w.id, { paused: next });
      }
      paintLive.current = null;
      setPaint(null);
    };
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [painting, rows, axis, onPatch]);

  const carrying = !!carry;
  useEffect(() => {
    if (!carrying || !axis) return;
    let raf = 0;
    const step = () => {
      const c = carryLive.current;
      if (c) {
        // Auto-scroll: the card you're aiming at is often off-screen.
        const dTop = c.y, dBot = window.innerHeight - c.y;
        if (dTop < EDGE_PX) window.scrollBy(0, -EDGE_SPEED * (1 - dTop / EDGE_PX));
        else if (dBot < EDGE_PX) window.scrollBy(0, EDGE_SPEED * (1 - dBot / EDGE_PX));
        const sc = scrollRef.current;
        if (sc) {
          const r = sc.getBoundingClientRect();
          const dL = c.x - r.left, dR = r.right - c.x;
          if (dL < EDGE_PX) sc.scrollLeft -= EDGE_SPEED * (1 - dL / EDGE_PX);
          else if (dR < EDGE_PX) sc.scrollLeft += EDGE_SPEED * (1 - dR / EDGE_PX);
        }
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);

    const onMove = (e: PointerEvent) => {
      const c = carryLive.current;
      if (!c) return;
      const moved = c.moved || Math.abs(e.clientX - c.x) > DRAG_SLOP_PX || Math.abs(e.clientY - c.y) > DRAG_SLOP_PX;
      // The ghost is pointer-events:none, so this reads what's underneath it.
      const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
      let toPerson: string | null = null;
      let toDay: number | null = null;
      let ok = false;
      if (c.kind === "reassign") {
        toPerson = el?.closest<HTMLElement>("[data-tl-person]")?.dataset.tlPerson ?? null;
        // A linked title's proofer is a real Assignment row, so it can only move
        // to somebody on the roster. A free-text name is just a string and can
        // go anywhere a group exists.
        const w = rows.find((r) => r.id === c.id);
        const real = !!w && (w.assigned_proofers || []).some((a) => a.name === c.from);
        ok = !!toPerson && toPerson !== c.from
          && (!real || !!memberByName.get(toPerson.trim().toLowerCase()));
      } else {
        const tr = trackRef.current?.getBoundingClientRect();
        if (tr && tr.width > 0 && el?.closest(".tl-grid")) {
          const i = Math.floor(((e.clientX - tr.left) / tr.width) * axis.total);
          if (i >= 0 && i < axis.total) { toDay = i; ok = true; }
        }
      }
      const next = { ...c, x: e.clientX, y: e.clientY, moved, toPerson, toDay, ok };
      carryLive.current = next;
      setCarry(next);
    };

    const onUp = () => {
      const c = carryLive.current;
      carryLive.current = null;
      setCarry(null);
      cancelAnimationFrame(raf);
      // Only a chip drag can collide with a click; a reassign starts on a handle
      // that has no click of its own.
      if (c?.moved && c.kind === "place") justCarried.current = c.id;
      if (!c || !c.moved || !c.ok) return;
      const w = rows.find((r) => r.id === c.id);
      if (!w) return;
      if (c.kind === "reassign" && c.from && c.toPerson) {
        const target = memberByName.get(c.toPerson.trim().toLowerCase());
        const a = (w.assigned_proofers || []).find((x) => x.name === c.from);
        const patch: { proofer?: string | null; paused?: Record<string, string[]> } = {};
        if (a) {
          if (!target) return;
          // ONE field. Scope (whole project, or that exact file subset) is only
          // recomputed when scope/scope_files are sent, so a like-for-like swap
          // is exactly this and nothing else.
          reassignM.mutate({ id: a.assignment_id, patch: { member_id: target.id } });
        } else {
          patch.proofer = c.toPerson;
        }
        // Parked days travel with the title. They're keyed by NAME, so leaving
        // them behind would strand them under a proofer who no longer has the
        // work — invisible, and the title would silently re-inflate to its
        // unparked rate on its new owner.
        const parked = w.paused?.[c.from];
        if (parked?.length) {
          const paused = { ...(w.paused || {}) };
          delete paused[c.from];
          paused[c.toPerson] = [...new Set([...(paused[c.toPerson] || []), ...parked])].sort();
          patch.paused = paused;
        }
        if (Object.keys(patch).length) onPatch(w.id, patch);
      } else if (c.kind === "place" && c.toDay != null) {
        const from = new Date(axis.first.getTime() + c.toDay * MS);
        const to = new Date(from.getTime() + (PLACED_DAYS - 1) * MS);
        onPatch(w.id, { start_date: iso(from), delivery_date: iso(to) });
        setFlashId(w.id);
      }
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [carrying, rows, axis, onPatch, memberByName, reassignM]);

  useEffect(() => {
    if (!flashId) return;
    const id = window.setTimeout(() => setFlashId(null), 1400);
    return () => window.clearTimeout(id);
  }, [flashId]);

  const beginCarry = (e: React.PointerEvent, kind: CarryState["kind"], w: Workflow, from?: string) => {
    if (e.button !== 0) return;
    e.preventDefault(); e.stopPropagation();
    const st: CarryState = {
      kind, id: w.id, label: w.name || "Untitled", from,
      x: e.clientX, y: e.clientY, moved: false, ok: false,
    };
    carryLive.current = st;
    setCarry(st);
  };

  const pxPerDay = axis && trackPx ? trackPx / axis.total : 0;
  const showDayNums = pxPerDay >= DAY_NUM_PX;
  const showLoadNums = pxPerDay === 0 || pxPerDay >= LOAD_NUM_PX;
  // Every day in the span, for the numbered strip. Cheap: the axis is weeks-to-
  // months in practice, and the gridlines themselves are pure CSS (no nodes).
  const days = useMemo(() => {
    const out: Date[] = [];
    if (!axis) return out;
    for (let i = 0; i < axis.total; i++) out.push(new Date(axis.first.getTime() + i * MS));
    return out;
  }, [axis]);
  // Widen (and scroll) rather than let days collapse — see MIN_DAY_PX.
  const gridStyle = {
    // Reserve the name column's ACTUAL width, not a constant. It's been
    // user-resizable since the frozen-column change, so a fixed 180 let a wide
    // name column eat into the day columns and squeeze them below MIN_DAY_PX —
    // the very collapse that floor exists to prevent.
    minWidth: `max(720px, ${Math.round((axis?.total ?? 1) * MIN_DAY_PX) + nameW}px)`,
    // Drive the day / week gridlines + weekend bands as CSS gradients: exact
    // alignment with the percentage geometry, and zero DOM per day.
    "--tl-day": `${100 / (axis?.total ?? 1)}%`,
    "--tl-week": `${700 / (axis?.total ?? 1)}%`,
    "--tl-name-w": `${nameW}px`,
  } as CSSProperties;

  /** The span a row is showing right now — the dragged preview if it's the one
   *  under the pointer, else its stored dates. */
  function shown(w: Workflow, span: Span): Span {
    if (!drag || drag.id !== w.id || !drag.days) return span;
    const shift = drag.days * MS;
    if (drag.mode === "move") {
      return { ...span, from: new Date(drag.from.getTime() + shift), to: new Date(drag.to.getTime() + shift) };
    }
    if (drag.mode === "start") {
      // Can't drag the start past the delivery — a bar is at least one day.
      const from = new Date(Math.min(drag.from.getTime() + shift, drag.to.getTime()));
      return { from, to: drag.to, inferred: false };   // setting a start makes it real
    }
    const to = new Date(Math.max(drag.to.getTime() + shift, drag.from.getTime()));
    return { ...span, to };
  }

  function beginDrag(e: React.PointerEvent, w: Workflow, span: Span, mode: DragMode) {
    e.preventDefault();
    e.stopPropagation();
    setDrag({ id: w.id, mode, from: span.from, to: span.to, inferred: span.inferred,
              originX: e.clientX, days: 0, moved: false });
  }

  useEffect(() => {
    if (!drag) return;
    const perDay = pxPerDay || MIN_DAY_PX;
    const onMove = (e: PointerEvent) => {
      const dx = e.clientX - drag.originX;
      setDrag((d) => (d ? { ...d, days: Math.round(dx / perDay), moved: d.moved || Math.abs(dx) > DRAG_SLOP_PX } : d));
    };
    const onUp = () => {
      setDrag((d) => {
        if (d && d.moved && d.days) {
          const span = { from: d.from, to: d.to, inferred: d.inferred };
          const next = (() => {
            const shift = d.days * MS;
            if (d.mode === "move") return { from: new Date(d.from.getTime() + shift), to: new Date(d.to.getTime() + shift) };
            if (d.mode === "start") return { from: new Date(Math.min(d.from.getTime() + shift, d.to.getTime())), to: d.to };
            return { from: d.from, to: new Date(Math.max(d.to.getTime() + shift, d.from.getTime())) };
          })();
          const patch: { start_date?: string | null; delivery_date?: string | null } = {};
          if (+next.to !== +span.to) patch.delivery_date = iso(next.to);
          // An inferred start isn't a stored date, so a plain MOVE mustn't
          // invent one — the derived start simply follows the new delivery.
          // Dragging the start edge is an explicit act, so that does set it.
          if (+next.from !== +span.from && (!d.inferred || d.mode === "start")) patch.start_date = iso(next.from);
          if (Object.keys(patch).length) onPatch(d.id, patch);
        }
        return null;
      });
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [drag, pxPerDay, onPatch]);

  // Below every hook, deliberately. This used to sit ~100 lines up, before the
  // `days` memo and the drag effect — so a filter that left no dated rows made
  // the component render two fewer hooks than the render before it, and React
  // tore the page down with "rendered more hooks than during the previous
  // render". An early return is only safe once nothing below it is a hook.
  if (!axis) {
    return (
      <div className="tl-empty">
        No project has a delivery date yet, so there's nothing to place on a timeline.
        Set a delivery date on the board and it'll appear here.
      </div>
    );
  }

  const pct = (d: Date) => ((d.getTime() - axis.first.getTime()) / MS / axis.total) * 100;
  /** A bar covers whole days, so its right edge is the END of the delivery day. */
  const barStyle = (s: Span) => ({
    left: `${pct(s.from)}%`,
    width: `${Math.max(1.2, pct(new Date(s.to.getTime() + MS)) - pct(s.from))}%`,
  });

  const dayISO = (i: number) => iso(new Date(axis.first.getTime() + i * MS));
  const dayLabel = (i: number) => {
    const d = new Date(axis.first.getTime() + i * MS);
    return `${d.getDate()} ${MON[d.getMonth()]}`;
  };
  /** hours → "2.4"; load on a person → "80%"; load on anything else →
   *  "1.6x" proofers needed. Same division by the norm; only the reading
   *  changes, which is what makes "workload" mean something on a status. */
  const fmtVal = (v: number, isPerson: boolean) =>
    mode === "hours" ? v.toFixed(1)
      : isPerson ? `${Math.round((v / norm) * 100)}%`
        : `${(v / norm).toFixed(1)}\u00d7`;

  /** A proofer's non-working days, when the name matches a roster member. A
   *  free-text name that isn't on the roster simply can't have days off. */
  const memberFor = (name: string) => memberByName.get(name.trim().toLowerCase());

  // --- parking a title for a proofer ---------------------------------------
  // A day on a bar belongs to (title x proofer), so switching it off is an edit
  // to the ASSIGNMENT, not the job: on a team-proofed title one person can step
  // away for three days while the other carries on, and the title keeps moving.

  /** Who a pause on this row applies to under the current lens. On a proofer's
   *  own card that's exactly them. On the status / client lenses there's no
   *  person in scope, so it's everyone booked on the row — parking it there
   *  parks the whole team, which the tooltip says out loud. */
  const pauseTargets = (w: Workflow, person: string | null): string[] =>
    person ? [person] : peopleOf(w);

  const idxOf = (d: string) => Math.round((parseD(d).getTime() - axis.first.getTime()) / MS);
  const pausedFor = (w: Workflow, person: string): Set<number> =>
    new Set((w.paused?.[person] || []).map(idxOf));
  /** Days already lost to a person-wide day off — shown blocked on the bar and
   *  NOT clickable, because "pausing" one would set a state that changes nothing. */
  const blockedFor = (person: string): Set<number> =>
    new Set((memberFor(person)?.unavailable || []).map(idxOf));


  // An arrow bound after the `!axis` guard, not a hoisted declaration — a
  // declaration could be called before the guard, so TS won't narrow inside it.
  const loadStrip = (label: string, vals: number[], lost: number, person: string | null) => {
    const max = Math.max(...vals, person ? norm * 1.05 : 0.0001);
    const m = person ? memberFor(person) : undefined;
    const off = new Set((m?.unavailable || []).map((d) =>
      Math.round((parseD(d).getTime() - axis.first.getTime()) / MS)));
    return (
      <div className={`tl-row tl-load${person && m ? " clickable" : ""}`} key={`load-${label}`}>
        <div className="tl-name muted">
          <span className="tl-loadkey">{mode === "hours" ? "h/day" : person ? "% day" : "prf"}</span>
          {lost > 0.05 && <span className="tl-lost" title="These hours have no available day left to land on">{lost.toFixed(1)} h unplaced</span>}
        </div>
        {/* The summary line is the ONLY trigger. Tracking the whole card meant
            the column appeared whenever the pointer crossed it — while reading
            titles, while reaching for a bar — which was more distraction than
            help. Asking about a day now means pointing at that day's total. */}
        <div className="tl-loadcells" onMouseLeave={() => setHoverDay(null)}>
          {hoverDay != null && (() => {
            const tot = vals[hoverDay] ?? 0;
            return (
              <>
                <div className="tl-mark" style={{ left: `${((hoverDay + 0.5) / axis.total) * 100}%` }} />
                <div className={`tl-read sum${person && tot > norm ? " over" : ""}`}
                  style={{ left: `${((hoverDay + 0.5) / axis.total) * 100}%` }}>
                  {fmtVal(tot, !!person)}
                </div>
              </>
            );
          })()}
          {vals.map((v, i) => {
            const isOff = off.has(i);
            const ratio = v / norm;
            const band = !person ? "" : ratio > 1 ? " over" : ratio > 0.8 ? " near" : " under";
            return (
              <div key={i}
                // No weekend / week classes any more: the strip inherits the
                // same gridline gradient the bars sit on, so those are drawn
                // once, in one place, and can't drift a day out of step.
                className={`tl-cell${v > 0 ? band : " empty"}${isOff ? " off" : ""}${hoverDay === i ? " hi" : ""}`}
                // Mixed into the surface rather than laid over it at alpha:
                // the cells now sit on the same gridline gradient as the bars,
                // and a translucent fill would let the day lines print through
                // the figure.
                style={!person && v > 0
                  ? { background: `color-mix(in srgb, var(--accent) ${Math.round(Math.max(14, Math.min(100, (v / max) * 100)))}%, var(--surface))`,
                      color: v / max > 0.55 ? "#fff" : undefined }
                  : undefined}
                onMouseEnter={() => setHoverDay(i)}
                onClick={person && m ? () => toggleAvail.mutate({ member: m, day: dayISO(i) }) : undefined}
                title={isOff
                  ? `${person} · ${dayLabel(i)} · unavailable — click to switch back on`
                  : `${label} · ${dayLabel(i)} · ${v.toFixed(2)} audio-h/day${person ? ` = ${Math.round(ratio * 100)}% of a ${norm} h day` : ""}${person && m ? ` — click to mark ${person} unavailable` : ""}`}>
                {isOff || v <= 0 || !showLoadNums ? "" : fmtVal(v, !!person)}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className={`tl${carry?.moved ? " carrying" : ""}`}>
      <div className="tl-head">
        <span className="tl-lbl">Group by</span>
        <div className="wf-seg" role="group" aria-label="Group the timeline by">
          {(["proofer", "status", "client"] as GroupBy[]).map((g) => (
            <button key={g} className={groupBy === g ? "on" : ""} onClick={() => setGroupBy(g)}>
              {g === "status" ? "Status" : g === "proofer" ? "Proofer" : "Client"}
            </button>
          ))}
        </div>
        <span className="tl-lbl tl-lbl-2">Show</span>
        <div className="wf-seg" role="group" aria-label="What the load strips show">
          <button className={mode === "hours" ? "on" : ""} onClick={() => setMode("hours")}>Hours</button>
          <button className={mode === "load" ? "on" : ""} onClick={() => setMode("load")}>Workload</button>
        </div>
        {/* The norm is the yardstick everything is read against, so it lives
            where you can see it take effect rather than buried in settings. */}
        <label className="tl-norm" title="Audio-hours one proofer gets through in a day">
          Norm
          <input type="number" min={0.25} max={24} step={0.25} value={norm}
            onChange={(e) => { const v = parseFloat(e.target.value); if (v > 0) setNorm(v); }} />
          <span>audio-h / day</span>
        </label>
        <span className="tl-count">{dated.length} scheduled{undated.length ? ` · ${undated.length} undated` : ""}</span>
      </div>

      <div className="tl-scroll" ref={scrollRef}>
        <div className="tl-grid" style={gridStyle}>
          {/* week ruler */}
          <div className="tl-axis">
            <div className="tl-axis-name">
              <span className="tl-rz" onMouseDown={startNameResize}
                title="Drag to resize the name column" role="separator" aria-orientation="vertical" />
            </div>
            <div className="tl-axis-track" ref={trackRef}>
              {axis.weeks.map((wk) => (
                <div key={weekKey(wk)} className="tl-tick" style={{ left: `${pct(wk)}%` }}>
                  <span>{wk.getDate()} {MON[wk.getMonth()]}</span>
                </div>
              ))}
              {showDayNums && (
                <div className="tl-days">
                  {days.map((d) => {
                    const wknd = d.getDay() === 0 || d.getDay() === 6;
                    const isToday = d.getTime() === axis.today.getTime();
                    const first = d.getDate() === 1;   // name the month where it turns over
                    return (
                      <span key={d.getTime()}
                        className={`tl-daynum${wknd ? " wknd" : ""}${isToday ? " today" : ""}${first ? " mstart" : ""}`}
                        style={{ left: `${pct(d)}%`, width: `var(--tl-day)` }}
                        title={`${d.getDate()} ${MON[d.getMonth()]} ${d.getFullYear()}`}>
                        {first ? MON[d.getMonth()] : d.getDate()}
                      </span>
                    );
                  })}
                </div>
              )}
              <div className="tl-today" style={{ left: `${pct(axis.today)}%` }} title="Today"><span>Today</span></div>
            </div>
          </div>

          {groups.map((grp) => {
            const gPerson = groupBy === "proofer" && !grp.key.startsWith("—") ? grp.key : null;
            const dropping = !!carry && carry.kind === "reassign" && carry.moved
              && carry.ok && carry.toPerson === gPerson;
            return (
            <div key={grp.key} className={`tl-group${dropping ? " droptarget" : ""}`}
              // The hit-test reads this attribute off whatever is under the
              // pointer, so the whole card is the target — not just its header.
              data-tl-person={gPerson ?? undefined}>
              {(() => {
                const person = groupBy === "proofer" && !grp.key.startsWith("—") ? grp.key : null;
                const label = groupBy === "status" ? statusMeta(grp.key).label : grp.key;
                const s = dist.seriesFor(grp.rows, person);
                const peak = Math.max(0, ...s.vals);
                // "Over capacity" is a warning, not a record. A day that has
                // already been worked cannot be re-planned, so a peak behind
                // today is history and raising a flag over it only teaches the
                // reader to ignore the flag. Only today and the days ahead of
                // it can set it.
                const from = Math.max(0, axis.todayIdx);
                const ahead = s.vals.slice(from);
                const overIdx = ahead.findIndex((v) => v > norm);
                const overOn = overIdx < 0 ? null
                  : new Date(axis.first.getTime() + (from + overIdx) * MS);
                // The peak still describes the whole span, because that is what
                // the bars beside it draw. Where it sits behind today it needs
                // to SAY so, or a busy past reads as a missing warning.
                const peakIdx = s.vals.indexOf(peak);
                const peakOn = peak > 0 ? new Date(axis.first.getTime() + peakIdx * MS) : null;
                const peakPast = peakIdx >= 0 && peakIdx < axis.todayIdx;
                return (
                  <div className="tl-gname">
                    <span className="tl-gin">
                      {/* what the group IS decides its marker: a person gets
                          their avatar, a status its dot, a client a monogram. */}
                      {groupBy === "status"
                        ? <span className="tl-gav" style={{ background: statusMeta(grp.key).bar }} aria-hidden="true" />
                        : <span className="tl-gav" style={{ background: avatarColor(grp.key) }} aria-hidden="true">{initials(grp.key)}</span>}
                      <span className="tl-gnametext" title={label}>{label}</span>
                      <span className="tl-gcount">{grp.rows.length} {grp.rows.length === 1 ? "title" : "titles"}</span>
                      {peak > 0 && (
                        <span className="tl-gstat"
                          title={peakOn
                            ? `Busiest day in view: ${peakOn.getDate()} ${MON[peakOn.getMonth()]}${peakPast ? " — already worked" : ""}`
                            : undefined}>
                          peak <b>{fmtVal(peak, !!person)}</b>
                        </span>
                      )}
                      {person && overOn && (
                        <span className="tl-gover"
                          title={`${fmtVal(Math.max(0, ...ahead), true)} against ${fmtVal(norm, true)} a day, first on ${overOn.getDate()} ${MON[overOn.getMonth()]}`}>
                          over capacity
                        </span>
                      )}
                    </span>
                  </div>
                );
              })()}
              <div className="tl-gbody" style={groupBy === "proofer" && !grp.key.startsWith("—")
                ? ({ "--tl-who": avatarColor(grp.key) } as CSSProperties) : undefined}>
              {grp.rows.map(({ w, span: stored }) => {
                const person = groupBy === "proofer" && !grp.key.startsWith("—") ? grp.key : null;
                const rate = dist.rateOf({ w, span: stored }, person);
                const dayRate = hoverDay == null ? 0 : dist.rateAt({ w, span: stored }, person, hoverDay);
                const span = shown(w, stored);
                const dragging = drag?.id === w.id && drag.moved;
                const m = statusMeta(w.status);
                // Past its delivery date and not finished — the thing a schedule
                // exists to surface.
                const late = span.to < axis.today && w.status !== "complete";
                const h = hoursOf(w);
                const who = peopleOf(w);
                const targets = pauseTargets(w, person);
                const paused = new Map(targets.map((pn) => [pn, pausedFor(w, pn)]));
                const blocked = new Map(targets.map((pn) => [pn, blockedFor(pn)]));
                const live = paint?.id === w.id ? paint : null;
                const inPaint = (i: number) => !!live
                  && i >= Math.min(live.anchor, live.day)
                  && i <= Math.max(live.anchor, live.day);
                // What the user is looking at RIGHT NOW: stored state, overridden
                // by whatever the in-flight drag has painted over it.
                const isPaused = (pn: string, i: number) => (inPaint(i) ? live!.on : paused.get(pn)!.has(i));
                const firstIdx = Math.round((span.from.getTime() - axis.first.getTime()) / MS);
                const spanDays = Math.round((span.to.getTime() - span.from.getTime()) / MS) + 1;
                const nPaused = targets.length
                  ? Array.from({ length: spanDays }, (_, j) => firstIdx + j)
                      .filter((i) => targets.some((pn) => isPaused(pn, i))).length
                  : 0;
                const lifted = carry?.kind === "reassign" && carry.id === w.id && carry.moved;
                return (
                  <div key={w.id} className={`tl-row${lifted ? " lifted" : ""}${flashId === w.id ? " flash" : ""}`}>
                    <div className="tl-name">
                      {/* The owner's accent, made grabbable: drag it onto another
                          proofer's card to hand the title over. It's here rather
                          than on the bar because the bar's three gestures are
                          spoken for, and because reassignment only means anything
                          on the lens where a card IS a person. */}
                      {person && (
                        <span className="tl-carry" title={`Drag onto another proofer to move ${w.name || "this title"} — same files, same dates`}
                          onPointerDown={(e) => beginCarry(e, "reassign", w, person)} />
                      )}
                      {/* The title is what opens things — the bar is now an
                          instrument, so its click belongs to the days. A linked
                          row goes to the project hub (as before); an unlinked one
                          opens its board row, which nothing else would reach. */}
                      <button className="tl-nlink tl-ntitle"
                        title={w.project_id ? `${w.name} — open the project hub` : `${w.name} — open on the board`}
                        onClick={() => (w.project_id ? onOpenProject(w.project_id) : onOpen(w.id))}>
                        {w.name || "Untitled"}
                      </button>
                      {/* Off the bar and into this column: the rate and the total
                          belong to the assignment, not to a position on the
                          calendar — and it leaves the bar free to be edited. */}
                      {!person && who.length > 0 && nameW >= NAME_WHO_W && (
                        <span className="tl-nwho" title={who.join(", ")}>
                          {who.slice(0, 3).map((pn) => (
                            <span key={pn} className="tl-nav" style={{ background: avatarColor(pn) }}>{initials(pn)}</span>
                          ))}
                          {who.length > 3 && <span className="tl-nmore">+{who.length - 3}</span>}
                        </span>
                      )}
                      {nPaused > 0 && (
                        <span className="tl-npaused"
                          title={`${nPaused} ${nPaused === 1 ? "day" : "days"} parked — the same hours over fewer days`}>
                          {nPaused} off
                        </span>
                      )}
                      {rate > 0 && <span className="tl-nrate">{rate.toFixed(1)} h/day</span>}
                      {h > 0 && nameW >= NAME_TOTAL_W && <span className="tl-ntot">{h.toFixed(1)} h</span>}
                    </div>
                    <div className="tl-track">
                      <div className="tl-todayline" style={{ left: `${pct(axis.today)}%` }} />
                      {hoverDay != null && (
                        <>
                          <div className="tl-mark" style={{ left: `${((hoverDay + 0.5) / axis.total) * 100}%` }} />
                          {/* A dash rather than nothing: "this title doesn't
                              touch that day" is an answer, and it keeps the set
                              of suspects explicit. */}
                          <div className={`tl-read${dayRate > 0 ? "" : " none"}`}
                            style={{ left: `${((hoverDay + 0.5) / axis.total) * 100}%` }}>
                            {dayRate > 0 ? dayRate.toFixed(1) : "—"}
                          </div>
                        </>
                      )}
                      {/* THREE affordances, three zones. Dragging across days to
                          park them and dragging the bar to reschedule it are the
                          same gesture on the same pixels, so they can't share a
                          surface: the grips resize, a band along the top moves,
                          and the day cells below it pause. */}
                      <div
                        className={`tl-bar${late ? " late" : ""}${span.inferred ? " inferred" : ""}${dragging ? " dragging" : ""}`}
                        style={{ ...barStyle(span), background: late ? undefined : m.bg, borderColor: late ? undefined : m.bar, color: late ? undefined : m.fg }}
                        title={dragging ? `${iso(span.from)} → ${iso(span.to)}` : [
                          w.name,
                          `${m.label}${late ? " · overdue" : ""}`,
                          span.inferred
                            ? `Delivery ${w.delivery_date} · no start date set (bar sized from ${h.toFixed(1)} h)`
                            : `${w.start_date} → ${w.delivery_date}`,
                          who.length ? who.join(", ") : "Unassigned",
                          h ? `${h.toFixed(1)} finished hours` : "Runtime TBD",
                        ].join("\n")}
                      >
                        {/* Edge handles: drag to change just that end. Their
                            own pointerdown stops the body's move-drag. */}
                        <span className="tl-grip left" title="Drag to change the start date"
                          onPointerDown={(e) => { if (e.button === 0) beginDrag(e, w, stored, "start"); }} />
                        <span className="tl-move" role="button" tabIndex={0}
                          title={`Drag to reschedule ${w.name || "this title"}`}
                          onPointerDown={(e) => { if (e.button === 0) beginDrag(e, w, stored, "move"); }}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(w.id); } }} />
                        <span className="tl-bardays">
                          {Array.from({ length: spanDays }, (_, j) => {
                            const i = firstIdx + j;
                            const parked = targets.filter((pn) => isPaused(pn, i));
                            const still = targets.filter((pn) => !isPaused(pn, i));
                            const off = parked.length > 0;
                            const all = off && !still.length;
                            const dead = targets.length > 0 && targets.every((pn) => blocked.get(pn)!.has(i));
                            const canPause = targets.length > 0 && !dead;
                            return (
                              <span key={i}
                                className={`tl-day${off ? (all ? " off" : " off part") : ""}${dead ? " blocked" : ""}${canPause ? "" : " inert"}`}
                                onPointerDown={canPause ? (e) => {
                                  if (e.button !== 0) return;
                                  e.preventDefault(); e.stopPropagation();
                                  const st: PaintState = { id: w.id, targets, anchor: i, day: i, on: !off };
                                  paintLive.current = st;
                                  setPaint(st);
                                } : undefined}
                                // Siblings, so entering one while the button is
                                // down is all the tracking a paint needs — and it
                                // clamps to the span for free.
                                onPointerEnter={live ? () => {
                                  const st = { ...live, day: i };
                                  paintLive.current = st;
                                  setPaint(st);
                                } : undefined}
                                title={dead
                                  ? `${dayLabel(i)} · ${targets.join(", ")} already unavailable`
                                  : !targets.length
                                    ? `${dayLabel(i)} · nobody assigned, so there's no dedication to pause`
                                    : all
                                      ? `${dayLabel(i)} · parked for ${parked.join(", ")} — click to work it again`
                                      // Partly parked: name who is off and who is
                                      // still on it, or the tooltip would claim a
                                      // proofer is parked when they're working.
                                      : off
                                        ? `${dayLabel(i)} · parked for ${parked.join(", ")} · ${still.join(", ")} still on it — click to clear`
                                        : `${dayLabel(i)} · click, or drag across, to take ${targets.join(", ")} off this title${targets.length > 1 ? " (all of them — use the Proofer lens to park one)" : ""}`} />
                            );
                          })}
                        </span>
                        <span className="tl-grip right" title="Drag to change the delivery date"
                          onPointerDown={(e) => { if (e.button === 0) beginDrag(e, w, stored, "end"); }} />
                      </div>
                    </div>
                  </div>
                );
              })}
              {(() => {
                const person = groupBy === "proofer" && !grp.key.startsWith("—") ? grp.key : null;
                const s = dist.seriesFor(grp.rows, person);
                return loadStrip(groupBy === "status" ? statusMeta(grp.key).label : grp.key, s.vals, s.lost, person);
              })()}
              </div>
            </div>
            );
          })}

          {/* The whole slate, per day. Every title counted once whatever the
              lens, so the group strips add up to this rather than exceeding it
              when a title has two proofers. */}
          <div className="tl-group">
            <div className="tl-gname">
              <span className="tl-gin">
                <span className="tl-gav" style={{ background: "var(--text)" }} aria-hidden="true" />
                <span className="tl-gnametext">All work</span>
                <span className="tl-gsub">
                  peak {fmtVal(Math.max(0, ...dist.total.vals), false)} {mode === "hours" ? "h/day" : "proofers"} across the slate
                </span>
              </span>
            </div>
            <div className="tl-gbody">{loadStrip("Total", dist.total.vals, dist.total.lost, null)}</div>
          </div>

          {/* Per-person capacity by WEEK. Redundant when you're already grouped
              by proofer — the group strips say the same thing, per day — so it
              only appears on the other lenses, where nothing else answers
              "is anyone overloaded". Fed by the same distribution, so days off
              are respected here too. */}
          {groupBy !== "proofer" && capacity.size > 0 && (
            <div className="tl-group tl-cap">
              <div className="tl-gname">
                <span className="tl-gin">
                  <span className="tl-gav" style={{ background: "var(--fg-2)" }} aria-hidden="true" />
                  <span className="tl-gnametext">Capacity</span>
                  <span className="tl-gsub">booked hours per week · ~{WEEK_CAPACITY_H} h is a full week</span>
                </span>
              </div>
              <div className="tl-gbody">
              {[...capacity.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([person, weeks]) => (
                <div key={person} className="tl-row">
                  <div className="tl-name muted" title={person}>{person}</div>
                  <div className="tl-track">
                    <div className="tl-todayline" style={{ left: `${pct(axis.today)}%` }} />
                    {axis.weeks.map((wk) => {
                      const booked = weeks.get(weekKey(wk)) ?? 0;
                      if (booked < 0.05) return null;
                      const load = Math.min(1, booked / WEEK_CAPACITY_H);
                      const over = booked > WEEK_CAPACITY_H;
                      // The figure is ALWAYS shown, not just when it trips the
                      // threshold: WEEK_CAPACITY_H is an assumption, and a strip
                      // that only speaks up when a guessed limit fires tells you
                      // nothing the rest of the time.
                      return (
                        <div key={weekKey(wk)} className={`tl-cap-cell${over ? " over" : ""}`}
                          style={{ left: `${pct(wk)}%`, width: `${(7 / axis.total) * 100}%`, "--load": `${0.25 + load * 0.75}` } as CSSProperties}
                          title={`${person} · week of ${wk.getDate()} ${MON[wk.getMonth()]} · ${booked.toFixed(1)} booked hours of ~${WEEK_CAPACITY_H} h${over ? " — over capacity" : ""}`}>
                          {booked >= 0.5 ? `${Math.round(booked)}h` : ""}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* What you're carrying, under the pointer. `pointer-events: none` so the
          hit-test underneath keeps reading the card / grid, not the ghost. */}
      {carry?.moved && (
        <div className={`tl-ghost${carry.ok ? " ok" : ""}`} style={{ left: carry.x, top: carry.y }}>
          {carry.kind === "reassign"
            ? (carry.ok ? `Move to ${carry.toPerson}` : `Drop on a proofer's card`)
            : (carry.ok && carry.toDay != null
              ? `${dayLabel(carry.toDay)} → ${dayLabel(Math.min(axis.total - 1, carry.toDay + PLACED_DAYS - 1))}`
              : "Drop on the timeline to schedule")}
          <b>{carry.label}</b>
        </div>
      )}

      {undated.length > 0 && (
        <div className="tl-undated">
          <div className="tl-undated-h">
            {undated.length} project{undated.length === 1 ? "" : "s"} with no delivery date — not schedulable yet
          </div>
          <div className="tl-chips">
            {undated.map((w) => (
              <button key={w.id} className={`tl-chip${carry?.id === w.id && carry.moved ? " lifted" : ""}`}
                onPointerDown={(e) => beginCarry(e, "place", w)}
                onClick={() => {
                  if (justCarried.current === w.id) { justCarried.current = null; return; }
                  onOpen(w.id);
                }}
                title={`Drag onto the timeline to schedule ${w.name || "this title"} (${PLACED_DAYS} days from where you drop it) — or click to open the row`}>
                <span className="tl-chipdot" style={{ background: statusMeta(w.status).bar }} />
                {w.name || "Untitled"}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
